<template>
  <section class="my-5">
    <h1 class="mt-8 mb-5 display-1">
      Eligible Voters for {{ electionAssociation.description }}
      Election
    </h1>
    <v-row>
      <v-col class="mb-0" cols="4">
        <v-autocomplete
          v-model="session"
          item-text="name"
          item-value="id"
          :items="sessions"
          filled
          clearable
          label="Session"
          hide-details
          disabled
        ></v-autocomplete
      ></v-col>

      <v-col class="m-0" cols="4">
        <v-autocomplete
          class="ma-0"
          v-model="programme"
          item-text="name"
          item-value="id"
          :items="programmes"
          filled
          clearable
          label="Programme"
          hide-details
          multiple
        ></v-autocomplete
      ></v-col>

      <v-col class="mb-0" cols="4">
        <v-autocomplete
          v-model="faculty"
          item-text="name"
          item-value="id"
          :items="faculties"
          filled
          clearable
          label="Faculty"
          hide-details
        ></v-autocomplete>
      </v-col>

      <v-col cols="4">
        <v-autocomplete
          v-model="department"
          item-text="name"
          item-value="id"
          :items="departments"
          filled
          clearable
          label="Department"
          hide-details
        ></v-autocomplete>
      </v-col>

      <v-col cols="4">
        <v-autocomplete
          v-model="course"
          item-text="name"
          item-value="id"
          :items="courses"
          filled
          clearable
          label="Course"
          hide-details
        ></v-autocomplete>
      </v-col>

      <v-col cols="4">
        <v-autocomplete
          v-model="state"
          item-text="text"
          item-value="id"
          :items="states"
          filled
          clearable
          label="State"
          hide-details
        ></v-autocomplete>
      </v-col>

      <v-col cols="4">
        <v-autocomplete
          v-model="lga"
          item-text="name"
          item-value="id"
          :items="lgas"
          filled
          clearable
          label="LGA"
          hide-details
        ></v-autocomplete>
      </v-col>
    </v-row>
    <p class="my-4">
      <strong>Description: </strong>
      <u>{{ getProgrammeName(programme) }}</u> students in
      <u>{{ getDepartmentName(department) }}</u> of
      <u>{{ getFacultyName(faculty) }}</u> from <u>{{ getLGAName(lga) }}</u>
      <u>{{ getStateName(state) }}</u> that registered {{future_sessions ? 'from' : 'for'}}
      <u>{{ getSessionName() }}</u> session
    </p>
    <v-divider class="my-7"></v-divider>
    <div class="d-flex">
      <v-btn
        large
        color="green"
        class="white--text mr-2"
        @click.prevent="fetchStudents"
        :loading="searching"
        :disabled="!session || searching"
      >
        Retrieve Students <v-icon right>mdi-download-circle</v-icon>
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        large
        class="white--text"
        color="red"
        v-if="foundEligible && !searching && !accrediting"
        @click.prevent="accreditVotersBeta"
        :loading="accreditingBeta"
        :disabled="accreditingBeta"
      >
        Accredit all {{ meta.total }} students
        <v-icon right>mdi-certificate</v-icon>
      </v-btn>
    </div>
    <div v-if="foundEligible" class="my-5">
      <v-divider class="my-7"></v-divider>

      <h3 class="mb-4 display-1">{{ meta.total }} eligible students</h3>

      <v-data-table
        :headers="headers"
        :items="eligible"
        class="elevation-1"
        hide-default-footer
        loading-text="Loading... Please wait"
        :footer-props="tableFooterProps"
      >
        <template v-slot:[`item.name`]="{ item }">
          <v-row align="center">
            <v-col v-if="$vuetify.breakpoint.mdAndUp" cols="3">
              <div class="voter-image">
                <v-img
                  class="shrink"
                  contain
                  :src="`${
                    item.passport
                      ? `https://uniuyo.edu.ng/eportals/passports/${item.passport}`
                      : '/favicon.png'
                  }`"
                  transition="scale-transition"
                  width="40"
                  height="40"
                >
                  <template v-slot:placeholder>
                    <v-row
                      class="fill-height ma-0"
                      align="center"
                      justify="center"
                    >
                      <v-progress-circular
                        indeterminate
                        color="grey lighten-3"
                      ></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
              </div>
            </v-col>
            <v-col>
              {{ item.name }}
            </v-col>
          </v-row>
        </template>
      </v-data-table>
      <div class="text-right pt-5">
        <v-pagination
          color="brown darken-2"
          total-visible="7"
          @input="fetchEligibleStudents"
          v-model="meta.current_page"
          :length="meta.last_page"
        ></v-pagination>
      </div>
    </div>
  </section>
</template>

<script>
import { formatName } from "../../../tools/format";

export default {
  props: {
    election: {
      type: String,
      default: "0",
    },
  },

  data: () => ({
    lgas: [],
    states: [],
    courses: [],
    sessions: [],
    faculties: [],
    programmes: [],
    departments: [],

    lga: null,
    state: null,
    course: null,
    session: null,
    future_sessions: null,
    faculty: null,
    programme: null,
    department: null,

    page: 1,
    pageCount: 0,
    eligible: [],
    allEligible: 0,
    searching: false,
    foundEligible: false,
    headers: [
      {
        text: "Student",
        align: "start",
        sortable: false,
        value: "name",
      },
      { text: "Reg. Number", value: "matric" },
      { text: "Department", value: "department" },
      { text: "Gender", value: "gender" },
      { text: "Phone", value: "phone" },
      { text: "Email", value: "email" },
    ],
    tableFooterProps: {
      itemsPerPageOptions: [-1],
    },
    meta: {
      total: 0,
      last_page: 0,
      current_page: 0,
    },

    query: null,
    accrediting: false,
    accreditingBeta: false,

    electionAssociation: {},
    electionSession: {},
  }),

  methods: {
    loadLGAs() {
      this.$axios.get("/lgas").then(async ({ data: lgas }) => {
        this.lgas = lgas;
      });
    },

    getLGAName() {
      const lga = this.lgas.find((l) => l.id == this.lga);
      return lga ? `${lga.name} LGA` : "any LGA";
    },

    loadStates() {
      this.$axios.get("/states").then(async ({ data: states }) => {
        // this.states = states;

        this.states = states.map((s) => {
          s.text = formatName(s.name);
          return s;
        });
      });
    },

    getStateName(id) {
      const state = this.states.find((s) => s.id == id);
      return state ? ` of ${state.text} State` : "";
    },

    loadCourses() {
      this.$axios.get("/courses").then(async ({ data: courses }) => {
        this.courses = courses;
      });
    },

    getCourseName() {
      return (
        this.courses.find((c) => c.id == this.course)?.name || "any course"
      );
    },

    loadFaculties() {
      this.$axios.get("/faculties").then(async ({ data: faculties }) => {
        this.faculties = faculties;
      });
    },

    getFacultyName() {
      const faculty = this.faculties.find((f) => f.id == this.faculty);
      return faculty ? `Faculty of ${faculty.name}` : "all faculties";
    },

    loadDepartments() {
      this.$axios.get("/departments").then(async ({ data: departments }) => {
        this.departments = departments;
      });
    },

    getDepartmentName() {
      const department = this.departments.find((p) => p.id == this.department);
      return department ? `Department of ${department.name}` : "any department";
    },

    loadProgrammes() {
      this.$axios.get("/programmes").then(async ({ data: programmes }) => {
        this.programmes = programmes;
      });
    },

    getProgrammeName() {
      return this.programmes.find((p) => p.id == this.programme)?.name || "all";
    },

    loadSessions() {
      this.$axios.get("/sessions").then(async ({ data: sessions }) => {
        this.sessions = [...sessions].reverse();
      });
    },

    getSessionName() {
      const session = this.sessions.find((s) => s.id == this.session);
      return session ? `${session.name}` : "any";
    },

    fetchStudents() {
      this.searching = true;
      this.fetchEligibleStudents(1);
    },

    composeQuery(page) {
      let query = `
        ${this.page ? `page=${page}&` : ""}
        ${this.session ? `session=${this.session}&` : ""}
        ${this.programme ? `programme=${this.programme}&` : ""}
        ${this.faculty ? `faculty=${this.faculty}&` : ""}
        ${this.department ? `department=${this.department}&` : ""}
        ${this.course ? `course=${this.course}&` : ""}
        ${this.state ? `state=${this.state}&` : ""}
        ${this.lga ? `lga=${this.lga}&` : ""}
        ${this.future_sessions ? `future_sessions=${this.future_sessions}&` : ""}
      `
        .slice(0, -1)
        .replace(/\s+/g, "");

      return String(query).slice(0, -1).replace(/\s+/g, "");
    },

    fetchEligibleStudents(page) {
      let query = this.composeQuery(page);
      this.query = query;
      this.searching = true;

      this.$axios
        .get(`/eligible?${query}`)
        .then(async ({ data: { data: eligible, meta } }) => {
          this.foundEligible = !!eligible.length;

          this.eligible = eligible;
          this.meta = meta;
        })
        .finally(() => {
          this.searching = false;
        });
    },

    accreditVoters() {
      this.accrediting = true;

      this.$axios
        .get(`/accredit/${this.election}?${this.query}`)
        .then(({ data: response }) => {
          const { accreditationCount } = response;
          this.$bus.$emit("snackbar", {
            type: "info",
            message: `${accreditationCount} voters accredited!`,
          });
        })
        .catch(() => {
          this.$bus.$emit("snackbar", {
            type: "error",
            message: `Something went wrong`,
          });
        })
        .finally(() => {
          this.accrediting = false;
        });
    },

    accreditVotersBeta() {
      this.accreditingBeta = true;

      this.$axios
        .get(`/beta/accredit/${this.election}?${this.query}`)
        .then(({ data: response }) => {
          const { accreditationCount } = response;
          this.$bus.$emit("snackbar", {
            type: "info",
            message: `${accreditationCount} voters accredited in beta!`,
          });
        })
        .catch(() => {
          this.$bus.$emit("snackbar", {
            type: "error",
            message: `Something went wrong`,
          });
        })
        .finally(() => {
          this.accreditingBeta = false;
        });
    },

    loadElectionDetails() {
      this.$axios
        .get(`/election/${this.election}`)
        .then(({ data: election }) => {
          const { association, session, future_sessions } = election;
          this.electionAssociation = association;
          this.electionSession = session;
          this.session = session.sSesId;
          this.future_sessions = future_sessions;
        })
        .catch((error) => {
          if (error.response.status == 404)
            this.$router.push({ name: "elections" });

          this.$bus.$emit("snackbar", {
            type: "error",
            message: "Election not found",
          });
        });
    },
  },

  mounted() {
    this.loadLGAs();
    this.loadStates();
    this.loadCourses();
    this.loadFaculties();
    this.loadDepartments();
    this.loadSessions();
    this.loadProgrammes();

    this.loadElectionDetails();
  },
};
</script>

<style lang="scss" scoped>
.voter-image {
  overflow: hidden;

  border-radius: 10%;
  width: 40px;
  height: 40px;
  margin-right: 0.5em;

  .v-image {
    transform: scale(1.2);
    object-fit: cover;
    object-position: top;
  }
}
</style>
